import { useEffect, useState } from 'react';
import type { CreateModelPricing, ModelPricing } from '@orqforge/shared';
import { apiClient } from '../lib/api-client';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { Input } from '../components/ui/Input';

const DEFAULT_MODELS: { modelId: string; label: string; costIn: number; costOut: number }[] = [
  { modelId: 'openai:gpt-4o-mini', label: 'OpenAI GPT-4o Mini', costIn: 0.15, costOut: 0.6 },
  { modelId: 'openai:gpt-4o', label: 'OpenAI GPT-4o', costIn: 2.5, costOut: 10 },
  { modelId: 'anthropic:claude-3-5-sonnet-latest', label: 'Anthropic Claude 3.5 Sonnet', costIn: 3, costOut: 15 },
  { modelId: 'anthropic:claude-3-5-haiku-latest', label: 'Anthropic Claude 3.5 Haiku', costIn: 0.8, costOut: 4 },
  { modelId: 'deepseek:deepseek-chat', label: 'DeepSeek Chat', costIn: 0.27, costOut: 1.1 },
  { modelId: 'deepseek:deepseek-v4-flash', label: 'DeepSeek V4 Flash', costIn: 0.14, costOut: 0.55 },
];

const blank: CreateModelPricing = { modelId: '', label: '', costInPer1M: 0, costOutPer1M: 0 };

function errText(err: unknown, fallback: string): string {
  if (!(err instanceof Error) || !err.message) return fallback;
  try {
    const parsed = JSON.parse(err.message) as { message?: unknown };
    if (typeof parsed.message === 'string' && parsed.message) return parsed.message;
    if (Array.isArray(parsed.message)) return parsed.message.map(String).join(', ');
  } catch {
    return err.message;
  }
  return err.message;
}

export function AdminSettingsPage() {
  const [pricingList, setPricingList] = useState<ModelPricing[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState<CreateModelPricing>(blank);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editCostIn, setEditCostIn] = useState(0);
  const [editCostOut, setEditCostOut] = useState(0);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try {
      setPricingList(await apiClient.listModelPricing());
      setError(null);
    } catch (err) {
      setError(errText(err, 'Failed to load pricing'));
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function addPricing() {
    if (!addForm.modelId || !addForm.label) return;
    try {
      await apiClient.createModelPricing(addForm);
      setShowAddForm(false);
      await load();
    } catch (err) {
      setError(errText(err, 'Failed to add model pricing'));
    }
  }

  async function saveEdit(row: ModelPricing) {
    try {
      await apiClient.updateModelPricing(row.id, { costInPer1M: editCostIn, costOutPer1M: editCostOut });
      setEditingId(null);
      await load();
    } catch (err) {
      setError(errText(err, 'Failed to update pricing'));
    }
  }

  async function deletePricing(id: string) {
    try {
      await apiClient.deleteModelPricing(id);
      await load();
    } catch (err) {
      setError(errText(err, 'Failed to delete pricing'));
    }
  }

  async function seedDefaults() {
    try {
      for (const model of DEFAULT_MODELS) {
        await apiClient.createModelPricing({
          modelId: model.modelId,
          label: model.label,
          costInPer1M: model.costIn,
          costOutPer1M: model.costOut,
        });
      }
      await load();
    } catch (err) {
      setError(errText(err, 'Failed to seed default pricing'));
      await load();
    }
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Platform Settings</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Global configuration that applies to all tenants. Only platform admins can make changes.
        </p>
      </div>
      <Card>
        <CardContent className="grid gap-4 pt-6">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-base font-semibold">Model pricing</h2>
            <Button
              size="sm"
              onClick={() => {
                setAddForm(blank);
                setShowAddForm(true);
              }}
            >
              Add model
            </Button>
          </div>
          <p className="text-sm text-gray-500">
            Per-million-token prices used for cost tracking across all tenants. Rows here override the built-in fallback defaults. Changes apply to future agent runs only.
          </p>
          {error ? (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">
              {error}
            </div>
          ) : null}
          {showAddForm ? (
            <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
              <div className="grid max-w-lg gap-3 sm:grid-cols-2">
                <label className="grid gap-1 text-sm sm:col-span-2">
                  <span className="text-xs text-gray-500">Model ID</span>
                  <Input
                    placeholder="openai:gpt-4o"
                    value={addForm.modelId}
                    onChange={(e) => setAddForm((f) => ({ ...f, modelId: e.target.value }))}
                  />
                </label>
                <label className="grid gap-1 text-sm sm:col-span-2">
                  <span className="text-xs text-gray-500">Label</span>
                  <Input
                    placeholder="OpenAI GPT-4o"
                    value={addForm.label}
                    onChange={(e) => setAddForm((f) => ({ ...f, label: e.target.value }))}
                  />
                </label>
                <label className="grid gap-1 text-sm">
                  <span className="text-xs text-gray-500">Cost in (per 1M tokens)</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={addForm.costInPer1M}
                    onChange={(e) => setAddForm((f) => ({ ...f, costInPer1M: Number(e.target.value) }))}
                  />
                </label>
                <label className="grid gap-1 text-sm">
                  <span className="text-xs text-gray-500">Cost out (per 1M tokens)</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={addForm.costOutPer1M}
                    onChange={(e) => setAddForm((f) => ({ ...f, costOutPer1M: Number(e.target.value) }))}
                  />
                </label>
              </div>
              <div className="mt-3 flex gap-2">
                <Button size="sm" disabled={!addForm.modelId || !addForm.label} onClick={() => void addPricing()}>
                  Add
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setShowAddForm(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : null}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs text-gray-500 dark:border-gray-800">
                  <th className="py-2 pr-3">Model</th>
                  <th className="py-2 pr-3 text-right">Cost in ($/1M tok)</th>
                  <th className="py-2 pr-3 text-right">Cost out ($/1M tok)</th>
                  <th className="py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pricingList.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="py-6 text-center text-sm text-gray-500">
                      No custom pricing. Using fallback defaults
                    </td>
                  </tr>
                ) : (
                  pricingList.map((row) => (
                    <tr key={row.id} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-2 pr-3">
                        <div className="font-medium">{row.label}</div>
                        <div className="text-xs text-gray-500">{row.modelId}</div>
                      </td>
                      <td className="py-2 pr-3 text-right">
                        {editingId === row.id ? (
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            className="ml-auto w-24 text-right"
                            value={editCostIn}
                            onChange={(e) => setEditCostIn(Number(e.target.value))}
                          />
                        ) : (
                          row.costInPer1M
                        )}
                      </td>
                      <td className="py-2 pr-3 text-right">
                        {editingId === row.id ? (
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            className="ml-auto w-24 text-right"
                            value={editCostOut}
                            onChange={(e) => setEditCostOut(Number(e.target.value))}
                          />
                        ) : (
                          row.costOutPer1M
                        )}
                      </td>
                      <td className="py-2 text-right">
                        {editingId === row.id ? (
                          <div className="flex justify-end gap-1">
                            <Button size="sm" variant="ghost" onClick={() => void saveEdit(row)}>
                              Save
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <div className="flex justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setEditingId(row.id);
                                setEditCostIn(row.costInPer1M);
                                setEditCostOut(row.costOutPer1M);
                              }}
                            >
                              Edit
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-600" onClick={() => void deletePricing(row.id)}>
                              Delete
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {pricingList.length === 0 ? (
            <Button size="sm" variant="ghost" className="w-fit" onClick={() => void seedDefaults()}>
              Seed from defaults
            </Button>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
